import CustomPagination from '@/platform/common/components/CustomPagination/CustomPagination';
import NoDataRow from '@/platform/common/components/NoDataRow/NoDataRow';
import SearchBarFilter from '@/platform/common/components/SearchBarFilter/SearchBarFilter';
import { useFilters } from '@/platform/hooks/useFilters';
import { useConfirmationStore } from '@/platform/stores/useConfirmationStore';
import {
    Button,
    Container,
    FilterBarToggle,
    Menu,
    MenuItem,
    MenuItems,
    MenuSeparator,
    Segment,
    SegmentContent,
    SegmentFooter,
    SegmentHeader,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
} from '@/platform/ui';
import { MenuButton } from '@headlessui/react';
import {
    EllipsisVerticalIcon,
    PencilIcon,
    PlusIcon,
    TrashIcon,
} from '@heroicons/react/24/outline';
import { Link, router, usePage } from '@inertiajs/react';

const IndexView = () => {
    const { auth, target_groups } = usePage().props;
    const { filters, updateFilters, isSorted } = useFilters(['target_groups']);
    const confirm = useConfirmationStore(state => state.confirm);

    const confirmDeleteOrRestore = targetGroup =>
        confirm({
            title: targetGroup?.deleted_at
                ? 'Doelgroep terugzetten?'
                : 'Doelgroep archiveren?',
            description: targetGroup?.deleted_at
                ? 'Weet je zeker dat je deze doelgroep wilt terugzetten?'
                : 'Weet je zeker dat je deze doelgroep wilt archiveren?',
            confirmLabel: targetGroup?.deleted_at
                ? 'Terugzetten'
                : 'Archiveren',
            onConfirm: close => {
                deleteOrRestore(targetGroup);
                close();
            },
        });

    const deleteOrRestore = targetGroup => {
        if (targetGroup?.deleted_at) {
            router.post(
                `/target-groups/${targetGroup.id}/restore`,
                {},
                {
                    preserveScroll: true,
                    preserveState: true,
                }
            );

            return;
        }

        router.delete(`/target-groups/${targetGroup.id}`, {
            preserveScroll: true,
            preserveState: true,
        });
    };

    return (
        <Container>
            <Segment>
                <SegmentHeader
                    header={`Doelgroepen (${target_groups?.meta?.total || 0})`}
                >
                    {auth?.permissions?.includes('create target groups') && (
                        <Button
                            variant='primary'
                            as={Link}
                            href='/target-groups/create'
                        >
                            <PlusIcon className='size-5' />
                            Nieuwe doelgroep
                        </Button>
                    )}
                </SegmentHeader>
                <SegmentContent>
                    <SearchBarFilter only={['target_groups']}>
                        <FilterBarToggle
                            label='Toon gearchiveerd'
                            checked={filters?.show_archived == 1}
                            onChange={checked =>
                                updateFilters(
                                    'show_archived',
                                    checked ? '1' : '0'
                                )
                            }
                        />
                    </SearchBarFilter>

                    <Table striped>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell
                                    sorted={isSorted('name')}
                                    onClick={() =>
                                        updateFilters('sort', 'name')
                                    }
                                >
                                    Naam
                                </TableHeaderCell>
                                <TableHeaderCell>Groep(en)</TableHeaderCell>
                                <TableHeaderCell
                                    className='w-px'
                                    textAlign='center'
                                >
                                    Status
                                </TableHeaderCell>
                                <TableHeaderCell
                                    className='w-px'
                                    textAlign='center'
                                >
                                    Toon in filter
                                </TableHeaderCell>
                                <TableHeaderCell
                                    className='w-px'
                                    textAlign='right'
                                />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {target_groups?.meta?.total === 0 && (
                                <NoDataRow numberOfColumns={5} />
                            )}

                            {target_groups?.data?.map(targetGroup => (
                                <TableRow key={targetGroup.id}>
                                    <TableCell>
                                        {auth?.permissions?.includes(
                                            'update target groups'
                                        ) && !targetGroup?.deleted_at ? (
                                            <Link
                                                href={`/target-groups/${targetGroup.id}/edit`}
                                            >
                                                {targetGroup.name}
                                            </Link>
                                        ) : (
                                            targetGroup.name
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {targetGroup?.groups
                                            ?.map(group => group?.name)
                                            ?.join(', ')}
                                    </TableCell>
                                    <TableCell
                                        className='w-px'
                                        singleLine
                                        textAlign='center'
                                    >
                                        {targetGroup.is_status ? 'Ja' : 'Nee'}
                                    </TableCell>
                                    <TableCell
                                        className='w-px'
                                        singleLine
                                        textAlign='center'
                                    >
                                        {targetGroup.use_in_filter
                                            ? 'Ja'
                                            : 'Nee'}
                                    </TableCell>
                                    <TableCell
                                        className='w-px'
                                        singleLine
                                        textAlign='right'
                                    >
                                        {[
                                            'update target groups',
                                            'delete target groups',
                                        ]?.some(permission =>
                                            auth?.permissions?.includes(
                                                permission
                                            )
                                        ) && (
                                            <Menu>
                                                <MenuButton>
                                                    <EllipsisVerticalIcon className='size-5' />
                                                </MenuButton>
                                                <MenuItems anchor='bottom end'>
                                                    {auth?.permissions?.includes(
                                                        'update target groups'
                                                    ) &&
                                                        !targetGroup?.deleted_at && (
                                                            <>
                                                                <MenuItem
                                                                    as={Link}
                                                                    href={`/target-groups/${targetGroup.id}/edit`}
                                                                    icon={
                                                                        PencilIcon
                                                                    }
                                                                >
                                                                    Wijzigen
                                                                </MenuItem>

                                                                <MenuSeparator />
                                                            </>
                                                        )}
                                                    {auth?.permissions?.includes(
                                                        'delete target groups'
                                                    ) && (
                                                        <MenuItem
                                                            className='text-red-600'
                                                            onClick={() =>
                                                                confirmDeleteOrRestore(
                                                                    targetGroup
                                                                )
                                                            }
                                                            icon={TrashIcon}
                                                        >
                                                            {targetGroup?.deleted_at
                                                                ? 'Terugzetten'
                                                                : 'Archiveren'}
                                                        </MenuItem>
                                                    )}
                                                </MenuItems>
                                            </Menu>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </SegmentContent>
                <SegmentFooter>
                    <CustomPagination
                        meta={target_groups?.meta}
                        only={['target_groups']}
                    />
                </SegmentFooter>
            </Segment>
        </Container>
    );
};

export default IndexView;
